import {
  AppModal,
  Button,
  Input,
  LoadingSpinner,
  Typography,
} from "@/components/ui";
import { searchMattersInfiniteQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { cn } from "@/lib/utils/cn";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

interface SelectMatterForSignatureRequestModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedMatterId?: string;
  onMatterSelected: (matter: { matterId: string; matterTitle: string }) => void;
}

export const SelectMatterForSignatureRequestModal = ({
  open,
  setOpen,
  selectedMatterId,
  onMatterSelected,
}: SelectMatterForSignatureRequestModalProps) => {
  const [searchValue, setSearchValue] = useState("");
  const query = useDebouncedValue(searchValue, 250);
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...searchMattersInfiniteQueryOptions({
      statusCategories: ["pre-litigation", "litigation"],
      search: query,
      sort: "title",
      order: "asc",
      pageSize: 50,
    }),
    enabled: open,
  });

  const matters = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.data) ?? [])
        .filter((matter) => Boolean(matter.id))
        .map((matter) => ({
          matterId: matter.id,
          matterTitle: matter.title || "Untitled matter",
        }))
        .sort((a, b) => a.matterTitle.localeCompare(b.matterTitle)),
    [data?.pages],
  );

  useEffect(() => {
    if (!open) {
      setSearchValue("");
    }
  }, [open]);

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      size="md"
      title="Select matter"
      description="Choose the matter this signature request belongs to."
      className="h-[min(80vh,780px)] max-w-2xl"
      bodyClassName="px-2 pt-3 pb-2"
      bodyContained
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
        <Input
          maxLength={200}
          value={searchValue}
          onChange={(event) => {
            setSearchValue(event.target.value);
          }}
          placeholder="Search matters..."
        />

        <div className="min-h-0 flex-1 overflow-hidden rounded-xl border">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="h-full overflow-y-auto">
              <div className="space-y-2 p-3">
                {matters.length > 0 ? (
                  matters.map((matter) => {
                    const isSelected = matter.matterId === selectedMatterId;

                    return (
                      <button
                        key={matter.matterId}
                        type="button"
                        className={cn(
                          "hover:bg-primary/5 w-full rounded-lg border px-4 py-3 text-left transition-colors",
                          isSelected && "border-primary bg-primary/5",
                        )}
                        onClick={() => {
                          onMatterSelected(matter);
                          setOpen(false);
                        }}
                      >
                        <Typography className="font-semibold">
                          {matter.matterTitle}
                        </Typography>
                        <Typography className="text-muted-foreground mt-1 text-xs">
                          {isSelected ? "Currently selected" : "Select matter"}
                        </Typography>
                      </button>
                    );
                  })
                ) : (
                  <div className="flex h-40 items-center justify-center px-4 text-center">
                    <Typography className="text-muted-foreground text-sm">
                      {isError
                        ? "Unable to load matters. Please try again."
                        : "No matters found."}
                    </Typography>
                  </div>
                )}
                {hasNextPage && (
                  <Button
                    className="w-full"
                    variant="outline"
                    disabled={isFetchingNextPage}
                    onClick={() => {
                      void fetchNextPage();
                    }}
                  >
                    {isFetchingNextPage ? "Loading..." : "Load more matters"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppModal>
  );
};
